import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { signIn, completeNewPassword, PORTAL_ROUTE_BY_GROUP } from '../auth/cognito';
import './Login.css';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNew, setConfirmNew] = useState('');
  const [pendingUser, setPendingUser] = useState(null); // CognitoUser awaiting new password
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  function redirectAfterLogin(user) {
    const from = location.state?.from;
    const dest = from || PORTAL_ROUTE_BY_GROUP[user.group];
    if (!dest) {
      setError('Your account has no portal access assigned. Contact support.');
      return;
    }
    navigate(dest, { replace: true });
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await signIn(email.trim(), password);
      if (result.requiresNewPassword) {
        setPendingUser(result.cognitoUser);
      } else {
        redirectAfterLogin(result);
      }
    } catch (err) {
      setError(err.message || 'Sign in failed.');
    } finally {
      setBusy(false);
    }
  }

  async function onSetNewPassword(e) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmNew) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const user = await completeNewPassword(pendingUser, newPassword);
      redirectAfterLogin(user);
    } catch (err) {
      setError(err.message || 'Could not set new password.');
    } finally {
      setBusy(false);
    }
  }

  if (pendingUser) {
    return (
      <section className="login-page">
        <div className="login-card">
          <h1 className="login-title">Set a new password</h1>
          <p className="login-sub">Your account requires a new password before you can continue.</p>

          <form onSubmit={onSetNewPassword} className="login-form">
            <label className="login-field">
              <span>New password</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={10}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <span className="login-hint">Min. 10 characters, upper &amp; lowercase and a number.</span>
            </label>

            <label className="login-field">
              <span>Confirm new password</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                value={confirmNew}
                onChange={(e) => setConfirmNew(e.target.value)}
              />
            </label>

            {error && <p className="login-error">{error}</p>}

            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Set password & sign in'}
            </button>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="login-page">
      <div className="login-card">
        <h1 className="login-title">Sign in</h1>
        <p className="login-sub">Access your Stratos Standard portal.</p>

        <form onSubmit={onSubmit} className="login-form">
          <label className="login-field">
            <span>Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="login-field">
            <span>Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          {error && <p className="login-error">{error}</p>}

          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          <p style={{ margin: '4px 0 0', textAlign: 'right' }}>
            <Link to="/forgot-password" style={{ fontSize: '0.83rem', color: '#64748b' }}>
              Forgot password?
            </Link>
          </p>
        </form>

        <p className="login-foot">
          Don't have an account? <Link to="/signup">Create one for free</Link>
        </p>
      </div>
    </section>
  );
}
